import { useCallback, useEffect, useRef } from 'react';
import { isMobileDevice } from '../../utils/device/mobileDetection';

/**
 * Hook to keep an input element visible above the on-screen keyboard on mobile devices.
 *
 * The widget is rendered inside a fixed Shadow DOM host. Keyboard positioning belongs
 * to the fixed widget shells, not the host page body, so this hook only restores page
 * scroll if the browser tries to pan the underlying page for a focused widget input.
 */
export function useMobileKeyboardAdjustment(inputRef: React.RefObject<HTMLElement>) {
    const scrollPositionRef = useRef({ x: 0, y: 0 });
    const restoreScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isInputFocused = useCallback(() => {
        const input = inputRef.current;
        if (!input) return false;

        const root = input.getRootNode();
        if (typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) {
            return root.activeElement === input;
        }

        return document.activeElement === input;
    }, [inputRef]);

    const restorePageScroll = useCallback(() => {
        if (typeof window === 'undefined' || !isInputFocused()) return;

        window.scrollTo(scrollPositionRef.current.x, scrollPositionRef.current.y);
    }, [isInputFocused]);

    const focus = useCallback(() => {
        if (!isMobileDevice() || typeof window === 'undefined') return;

        scrollPositionRef.current = {
            x: window.scrollX || window.pageXOffset || 0,
            y: window.scrollY || window.pageYOffset || 0,
        };

        window.requestAnimationFrame(restorePageScroll);
        if (restoreScrollTimerRef.current !== null) {
            clearTimeout(restoreScrollTimerRef.current);
        }
        restoreScrollTimerRef.current = setTimeout(() => {
            restorePageScroll();
            restoreScrollTimerRef.current = null;
        }, 250);
    }, [restorePageScroll]);

    const blur = useCallback(() => {
        if (restoreScrollTimerRef.current !== null) {
            clearTimeout(restoreScrollTimerRef.current);
            restoreScrollTimerRef.current = null;
        }
    }, []);

    // Safety: cleanup on unmount
    useEffect(() => {
        return () => {
            blur();
        };
    }, [blur]);

    return { onFocus: focus, onBlur: blur };
}
